<template>
  <span id="bill-of-lading-detail-dialog" v-if="$can(permissions.OUTBOUND.VIEW_BILL_OF_LADINGS)">
    <i v-if="showIconGrid" class="el-icon-search header-info-dialog-value icon-action" id="btn-load-detail"
       @click="handleOpenDialog()" size="mini" icon="el-icon-plus"></i>
    <el-dialog
      :close-on-click-modal="false"
      :visible.sync="dialogVisible"
      width="85%">
      <span slot="title">
        <el-row :gutter="20">
          <el-col :span="12">
            <span class="el-dialog__title">
              {{ $t('customerService.billOfLading.title') }}
            </span>
          </el-col>
          <el-col :span="11" class="text-align-end">
            <generate-shipments-manually v-if="canGenerateShipments" :bill-of-lading-id="currentRow.id"/>
            <add-bill-of-lading-detail class="margin-r-10" @bolDetailAdded="loadDetails" v-if="currentRow.manual"
                                       :sub-account="currentRow.subaccount" :bill-of-lading-id="currentRow.id"/>
            <el-button id="btn-regenerate-bol" class="margin-r-10" v-show="canRegenerateBillOfLading" type="success"
                       plain size="mini" @click="regenerateBillOfLading">
              {{ $t('customerService.billOfLading.regenerate') }}
            </el-button>
          </el-col>
        </el-row>
      </span>
      <el-row :gutter="20">
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.bolNumber') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ billOfLading.currentRow.bolNumber }} </span>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.date') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ billOfLading.currentRow.date }} </span>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.shipments') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ billOfLading.currentRow.shipments }} </span>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.billTo') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list">
            <el-tooltip class="item" effect="dark"
                        :content="`${billOfLading.currentRow.billToAccountName} - ${billOfLading.currentRow.billToAccountNumber}`"
                        placement="top">
              <span> {{ billOfLading.currentRow.billToAccountName }} - {{
                  billOfLading.currentRow.billToAccountNumber
                }} </span>
            </el-tooltip>
          </span>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.consignee') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list">
            <el-tooltip class="item" effect="dark"
                        :content="`${billOfLading.currentRow.consigneeAccountName} - ${billOfLading.currentRow.consigneeAccountNumber}`"
                        placement="top">
              <span> {{
                  billOfLading.currentRow.consigneeAccountName
                }} - {{ billOfLading.currentRow.consigneeAccountNumber }} </span>
            </el-tooltip>
          </span>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.deliverTo') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list">
            <el-tooltip class="item" effect="dark"
                        :content="`${billOfLading.currentRow.deliverToAccountName} - ${billOfLading.currentRow.deliverToAccountNumber}`"
                        placement="top">
              <span> {{
                  billOfLading.currentRow.deliverToAccountName
                }} - {{ billOfLading.currentRow.deliverToAccountNumber }} </span>
            </el-tooltip>
          </span>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.units') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ billOfLading.currentRow.units }} </span>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('customerService.billOfLading.cubes') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ billOfLading.currentRow.cubes }} </span>
        </el-col>
      </el-row>
      <div>
        <span class="title-list">{{ $t('customerService.billOfLading.details') }}</span>
      </div>
      <el-form :model="editDetailForm" :rules="editDetailFormRules" ref="editDetailForm">
        <el-table
          stripe
          v-loading="billOfLading.loadingDetails"
          id="bill-of-lading-data-list"
          size="mini"
          tooltip-effect="dark"
          sortable
          width="80%"
          class="dark-blue-table"
          :empty-text="$t('common.noRecords')"
          :data="billOfLading.details"
        >
          <el-table-column
            type="index"
            :label="$t('customerService.billOfLading.sequence')"
            min-width="5"
            align="center"/>
          <el-table-column
            :label="$t('customerService.billOfLading.shipper')"
            align="left"
            header-align="center"
            :min-width="10"
          >
            <template slot-scope="scope">
              <el-tooltip class="item" effect="dark" :content="scope.row.shipper" placement="top">
                <span>{{ scope.row.shipper }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.productCode')"
            align="center"
            :min-width="5">
            <template slot-scope="scope">
              <span>{{ scope.row.productCode }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.productName')"
            align="left"
            header-align="center"
            :min-width="7">
            <template slot-scope="scope">
              <span> {{ scope.row.productName }} </span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.length')"
            align="center"
            :min-width="4">
            <template slot-scope="scope">
              <template v-if="!scope.row.edit">
                <span>{{ scope.row.length }}</span>
              </template>
              <template v-else>
                <el-input-number
                  v-model="editDetailForm.length"
                  v-numeric-validation
                  :controls="false"
                  :precision="2"
                  size="mini"
                  @focus="$event.target.select()"
                  :min="0" :max="999.99"
                  class="inline-input width-100p"
                  id="edit-form-length"
                  ref="length"
                ></el-input-number>
              </template>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.width')"
            align="center"
            :min-width="3">
            <template slot-scope="scope">
              <template v-if="!scope.row.edit">
                <span> {{ scope.row.width }} </span>
              </template>
              <template v-else>
                <el-input-number
                  v-model="editDetailForm.width"
                  v-numeric-validation
                  :controls="false"
                  :precision="2"
                  size="mini"
                  @focus="$event.target.select()"
                  :min="0" :max="999.99"
                  class="inline-input width-100p"
                  id="edit-form-width"
                  ref="width"
                ></el-input-number>
              </template>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.height')"
            align="center"
            :min-width="3">
            <template slot-scope="scope">
              <template v-if="!scope.row.edit">
                <span> {{ scope.row.height }} </span>
              </template>
              <template v-else>
                <el-input-number
                  v-model="editDetailForm.height"
                  v-numeric-validation
                  :controls="false"
                  :precision="2"
                  size="mini"
                  @focus="$event.target.select()"
                  :min="0" :max="999.99"
                  class="inline-input width-100p"
                  id="edit-form-height"
                  ref="height"
                ></el-input-number>
              </template>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.cubes')"
            align="center"
            :min-width="4">
            <template slot-scope="scope">
              <template v-if="!scope.row.edit">
                <span> {{ scope.row.cubes }} </span>
              </template>
              <template v-else>
                <el-input-number
                  v-model="editDetailForm.cubes"
                  v-numeric-validation
                  :controls="false"
                  :precision="2"
                  size="mini"
                  @focus="$event.target.select()"
                  @change="calculateTotalCubes()"
                  :min="0" :max="999.99"
                  class="inline-input width-100p"
                  id="edit-form-cubes"
                  ref="cubes"
                ></el-input-number>
              </template>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.units')"
            align="center"
            :min-width="4">
            <template slot-scope="scope">
              <template v-if="!scope.row.edit">
                <span> {{ scope.row.units }} </span>
              </template>
              <template v-else>
                <el-input-number
                  v-model="editDetailForm.totalUnits"
                  v-numeric-validation
                  :precision="0"
                  size="mini"
                  :min="0" :max="999"
                  @focus="$event.target.select()"
                  @change="calculateTotalCubes()"
                  class="inline-input width-100p"
                  id="form-number-units"
                  ref="unit"
                ></el-input-number>
              </template>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('customerService.billOfLading.totalCubes')"
            align="center"
            :min-width="4">
            <template slot-scope="scope">
              <template v-if="!scope.row.edit">
                <span> {{ scope.row.totalCubes }} </span>
              </template>
              <template v-else>
                <span> {{ totalCubes }} </span>
              </template>
            </template>
          </el-table-column>
          <el-table-column
            :min-width="2"
            :label="$t('common.options')"
            align="center"
            class-name="fixed-width">
              <template slot-scope="scope">
                <el-tooltip v-if="canEditBolDetail && !editingDetail"
                  class="item el-icon-delete-tooltip"
                  effect="dark"
                  :enterable="false"
                  :content="$t('common.edit')"
                  placement="top-start"
                >
                  <i class="el-icon-edit icon-action" @click="handleEditDetail(scope.row)"></i>
                </el-tooltip>
                <template v-else-if="scope.row.edit">
                  <el-button icon="el-icon-success" circle size="mini" @click="handleUpdateDetail(scope.row)"></el-button>
                  <el-button icon="el-icon-circle-close" circle size="mini" @click="clearEdit(scope.row)"></el-button>
                </template>
                <el-tooltip v-if="canEditBolDetail && !editingDetail"
                  class="item el-icon-delete-tooltip"
                  effect="dark"
                  :enterable="false"
                  :content="$t('common.delete')"
                  placement="top-start"
                >
                  <i class="el-icon-delete icon-action" @click="handleDeleteDetail(scope.row)"></i>
                </el-tooltip>
              </template>
            </el-table-column>
        </el-table>
      </el-form>
      <div id="bill-of-lading-dialog-footer" slot="footer" class="pagination-footer"
           v-show="billOfLading.totalDetails > 0">
        <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('customerService.billOfLading.total') }}</span>
          {{ billOfLading.totalDetails }}
        </span>
        <el-pagination
          @size-change="handleSearchChangeLimit"
          :page-sizes="sizes"
          :page-size="billOfLading.actualFilters.rows"
          :current-page.sync="billOfLading.actualFilters.page"
          @current-change="handleSearchChangePage"
          :total="billOfLading.totalDetails"
          layout="sizes, prev, pager, next, jumper"
        >
        </el-pagination>
      </div>
    </el-dialog>
  </span>
</template>

<script>
import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';
import AddBillOfLadingDetail from '@/views/customerCenter/billOfLading/components/AddBillOfLadingDetail';
import GenerateShipmentsManually from '@/views/customerCenter/billOfLading/components/GenerateShipmentsManually';

const billOfLadingGetDetailsAction = 'billOfLading/getDetails';

export default {
  name: 'BillOfLadingDetailDialog',
  components: {
    GenerateShipmentsManually,
    AddBillOfLadingDetail
  },
  computed: {
    ...mapGetters([
      'billOfLading'
    ]),
    canRegenerateBillOfLading() {
      return !this.currentRow.manual && this.currentRow.active && !this.currentRow.rated
        && !this.currentRow.invoiced && this.$can(this.permissions.OUTBOUND.EDIT_LOADS);
    },
    canGenerateShipments() {
      return this.currentRow.manual && this.currentRow.active && !this.currentRow.rated
        && !this.currentRow.invoiced && this.$can(this.permissions.OUTBOUND.EDIT_LOADS);
    },
    canEditBolDetail() {
      return true;
    }
  },
  props: {
    currentRow: {
      type: Object,
      default: null
    },
    showIconGrid: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      editing: false,
      editingDetail: false,
      totalCubes: 0,
      editDetailForm: {
        billOfLadingId: null,
        billOfLadingDetailId: null,
        length: null,
        width: null,
        height: null,
        cubes: null,
        totalUnits: null
      },
      editDetailFormRules: {
        billOfLadingId: [
          {
            required: true,
            message: this.$t('customerService.billOfLading.error.billOfLadingIdRequired'),
            trigger: 'submit'
          }
        ]
      },
      dialogVisible: false,
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES,
    };
  },
  methods: {
    handleSearchChangeLimit(val) {
      this.$store.dispatch('billOfLading/getDetails', {
        billOfLadingId: this.currentRow.id,
        page: this.billOfLading.actualFilters.page,
        rows: val,
        pageResults: true
      });
    },
    handleSearchChangePage(val) {
      this.$store.dispatch(billOfLadingGetDetailsAction, {
        billOfLadingId: this.currentRow.id,
        page: val,
        rows: this.billOfLading.actualFilters.rows,
        pageResults: true
      });
    },
    loadDetails() {
      this.$store.dispatch(billOfLadingGetDetailsAction, {
        billOfLadingId: this.currentRow.id,
        page: constants.TABLES.DEFAULT_PAGE,
        rows: constants.TABLES.DEFAULT_LIMIT,
        pageResults: true
      });
    },
    handleOpenDialog() {
      this.dialogVisible = true;
      this.loadDetails();
      this.$nextTick(() => {
        this.setDetail();
      });
    },
    calculateTotalCubes() {
      const result = this.editDetailForm.cubes * this.editDetailForm.totalUnits;
      this.totalCubes = result.toFixed(2);
    },
    setDetail() {
      return this.$store.dispatch('billOfLading/setRow', { row: this.currentRow });
    },
    async regenerateBillOfLading() {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      try {
        const response = await this.$store.dispatch('billOfLading/regenerate', this.currentRow.id);
        await this.$store.dispatch(billOfLadingGetDetailsAction, {
          billOfLadingId: this.currentRow.id,
          page: constants.TABLES.DEFAULT_PAGE,
          rows: constants.TABLES.DEFAULT_LIMIT,
          pageResults: true
        });
        if (response && response.message) {
          this.$message.success(response.message);
        }
      } finally {
        loading.close();
      }
    },
    showDeleteDetail() {
      return this.currentRow.manual;
    },
    handleEditDetail(row) {
      this.$store.dispatch('billOfLading/setEditingDetails', true);
      row.edit = true;
      this.editingDetail = true;

      this.editDetailForm.billOfLadingId = row.id;
      this.editDetailForm.billOfLadingDetailId = row.sequence;
      this.editDetailForm.length = row.length;
      this.editDetailForm.width = row.width;
      this.editDetailForm.height = row.height;
      this.editDetailForm.cubes = row.cubes;
      this.editDetailForm.totalUnits = row.units;

      this.calculateTotalCubes();

      this.$nextTick(() => {
      });
    },
    handleUpdateDetail(row) {
      return new Promise((resolve) => {
        this.$refs.editDetailForm.validate((valid) => {
          if (valid) {
            Message.closeAll();
            const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
            this.$store
              .dispatch('billOfLading/updateBolDetail', {
                billOfLadingId: this.currentRow.id,
                billOfLadingDetailId: row.sequence,
                billOfLadingDetail: this.editDetailForm
              })
              .then((response) => {
                loading.close();
                this.$message.success(response.message);
                this.clearEdit(row);
                this.$emit('bolDetailAdded');
                this.loadDetails();
                resolve(response);
              })
              .catch((error) => {
                loading.close();
                resolve(error);
              });
          } else {
            resolve(new Error(this.$t('common.invalid')));
          }
        });
      });
    },
    async handleDeleteDetail(row) {
      const confirm = await this.$confirm(this.$t('customerService.billOfLading.deleteDetailConfirmation'), { confirmButtonText: this.$t('common.yes') }).catch(() => {});
      if (confirm) {
        Message.closeAll();
        const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
        try {
          const response = await this.$store.dispatch('billOfLading/deleteDetail', {
            billOfLadingDetailId: row.sequence
          });
          loading.close();
          this.$message.success(response.message);
          this.loadDetails();
        } finally {
          loading.close();
        }
      }
    },
    clearEdit(row) {
      this.$store.dispatch('billOfLading/setEditingDetails', false);
      row.edit = false;
      this.editingDetail = false;
      this.$refs.editDetailForm.resetFields();
      this.editDetailForm.billOfLadingId = null;
      this.editDetailForm.billOfLadingDetailId = null;
      this.editDetailForm.length = null;
      this.editDetailForm.width = null;
      this.editDetailForm.height = null;
      this.editDetailForm.cubes = null;
      this.editDetailForm.units = null;
    }
  }
};
</script>
